import assert from "assert";
import type { Doc } from "building-types/doc";
import { docEntityIdFromString } from "building-types/doc/docEntityId";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import { allFaceCategories, FaceCategory } from "building-types/materials/faceCategories";
import { PlanLineStyle } from "building-types/settings/settingsTypesEnum";
import { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import type { ViewportViewType, ViewTemplateOverride, SheetGeometryCategoryStyle } from "building-types/views/planSets";
import {
  DEFAULT_ROOM_TAG_CAPITALIZATION,
  type ElevationViewTemplate,
  type EvaluatedViewport,
  type PlanViewTemplate,
  type SectionViewTemplate,
  type ViewTemplate,
} from "building-types/views/planSets";
import type { LineStyle } from "building-types/views/Primitive2DOutput";
import { SelectionMethod } from "building-types/views/SerializableSheetGeometrySelector";
import {
  sheetGeometry3DCategories,
  SheetGeometryCategory,
  sheetGeometryElevationAnnotationCategories,
} from "building-types/views/sheetGeometryCategory";
import { viewTemplateUICategoriesForViewType } from "building-types/views/sheetGeometryCategoryTools";
import type { SectionView } from "building-types/views/View3D";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { elevationDefaultCategoryStyles, planDefaultCategoryStyles } from "./plans/planStyleSheets";
import type { SheetGeometryCategoryStyleSheets } from "./plans/SheetGeometrySourceStyleOverrideTable";
import { SystemRoomTagTemplate } from "./systemDrawingTemplates";

const DEFAULT_ROOM_TAG_TEMPLATE_ID = docEntityIdFromString("CONTENT_TEMPLATE", SystemRoomTagTemplate.id);

export function viewTemplateNew({
  viewType,
  referenceType = BuiltinPlanViewType.FLOOR,
  name = "View Template",
  belowGradeRenderStyle = PlanLineStyle.NONE,
  categories = viewTemplateUICategoriesForViewType(viewType),
  viewTemplateOverrideIds = [],
  roomTagCapitalization = DEFAULT_ROOM_TAG_CAPITALIZATION,
  sortIndex = 0,
  roomTagTemplateId = DEFAULT_ROOM_TAG_TEMPLATE_ID,
  cutPlaneHeight = undefined,
  stairCutPlaneHeight = undefined,
  wallFinishCutPlaneHeight = undefined,
}: Pick<ViewTemplate, "viewType"> &
  Partial<Pick<ViewTemplate, "name" | "categories" | "viewTemplateOverrideIds" | "sortIndex">> &
  Partial<
    Pick<
      PlanViewTemplate,
      | "referenceType"
      | "roomTagCapitalization"
      | "roomTagTemplateId"
      | "cutPlaneHeight"
      | "stairCutPlaneHeight"
      | "wallFinishCutPlaneHeight"
    >
  > &
  Partial<Pick<ElevationViewTemplate, "belowGradeRenderStyle">>): ViewTemplate {
  switch (viewType) {
    case "PLAN_VIEW":
      return {
        ...docEntityBaseNew("VIEW_TEMPLATE"),
        name,
        viewType,
        categories,
        referenceType,
        viewTemplateOverrideIds,
        sortIndex,
        styleOverrides: {},
        roomTagCapitalization,
        roomTagTemplateId,
        cutPlaneHeight,
        stairCutPlaneHeight,
        wallFinishCutPlaneHeight,
      };
    case "ELEVATION_VIEW":
      return {
        ...docEntityBaseNew("VIEW_TEMPLATE"),
        name,
        viewType,
        categories,
        belowGradeRenderStyle,
        viewTemplateOverrideIds,
        sortIndex,
        styleOverrides: {},
      };
    case "SECTION_VIEW":
      return {
        ...docEntityBaseNew("VIEW_TEMPLATE"),
        name,
        viewType,
        categories,
        viewTemplateOverrideIds,
        sortIndex,
        styleOverrides: {},
      };
    case "VIEW_3D":
      return {
        ...docEntityBaseNew("VIEW_TEMPLATE"),
        name,
        viewType,
        categories,
        viewTemplateOverrideIds,
        sortIndex,
        styleOverrides: {},
      };
    case "DRAWING": {
      return {
        ...docEntityBaseNew("VIEW_TEMPLATE"),
        name,
        viewType,
        categories,
        viewTemplateOverrideIds,
        sortIndex,
        styleOverrides: {},
      };
    }
  }
}

export function viewTemplateOverrideNew({
  name = "View Template Override",
  labels = {},
  selectionMethod = SelectionMethod.BY_LABEL,
}: Partial<ViewTemplateOverride> = {}): ViewTemplateOverride {
  return {
    ...docEntityBaseNew("VIEW_TEMPLATE_OVERRIDE"),
    name,
    labels,
    selectionMethod,
  };
}

export function viewportSectionView(viewport: EvaluatedViewport, doc: Doc): SectionView | undefined {
  return doc.sectionViews[viewport.contentSourceId];
}

export function isElevationViewTemplate(viewTemplate: ViewTemplate): viewTemplate is ElevationViewTemplate {
  return viewTemplate.viewType === "ELEVATION_VIEW";
}

export function isSectionViewTemplate(viewTemplate: ViewTemplate): viewTemplate is SectionViewTemplate {
  return viewTemplate.viewType === "SECTION_VIEW";
}

export function viewTemplateContainsElevationAnnotation(viewTemplate: ViewTemplate): boolean {
  if (!isElevationViewTemplate(viewTemplate)) {
    return false;
  }

  for (const category of viewTemplate.categories) {
    if (sheetGeometryElevationAnnotationCategories.includes(category)) {
      return true;
    }
  }

  return false;
}

export function isLinework3DGeometryView(viewType: ViewTemplate["viewType"]) {
  return viewType === "SECTION_VIEW" || viewType === "ELEVATION_VIEW" || viewType === "VIEW_3D";
}

export function viewTemplateContainsLinework3D(viewTemplate: ViewTemplate): boolean {
  // We return ALL of the 3D geometry for all of the linework views (elevation, section, and 3D), even if the categories are disabled.
  // The categories are filtered outside of the plan set service to speed up toggling categories on and off on the main thread.
  return (
    isLinework3DGeometryView(viewTemplate.viewType) ||
    viewTemplate.categories.includes(SheetGeometryCategory.ROOF) ||
    viewTemplate.categories.includes(SheetGeometryCategory.SKYLIGHTS)
  );
}

export function faceCategoryToSheetGeometryCategory(category: FaceCategory): SheetGeometryCategory | undefined {
  switch (category) {
    case FaceCategory.CASEWORK:
      return SheetGeometryCategory.CASEWORK;
    case FaceCategory.CEILING:
      return SheetGeometryCategory.CEILING;
    case FaceCategory.DOOR_DECORATION:
    case FaceCategory.DOOR_EXTERIOR:
    case FaceCategory.DOOR_CURVED_WALL_EXTERIOR:
    case FaceCategory.DOOR_INTERIOR:
    case FaceCategory.DOOR_CURVED_WALL_INTERIOR:
      return SheetGeometryCategory.DOORS;
    case FaceCategory.CORNER_BOARD:
      return SheetGeometryCategory.CORNER_BOARD;
    case FaceCategory.FACADE_WALL_EXTERIOR:
      return SheetGeometryCategory.WALLS;
    case FaceCategory.FLOOR:
      return SheetGeometryCategory.FLOOR_FINISH;
    case FaceCategory.FLOOR_FRAMING:
      return SheetGeometryCategory.FLOOR_FRAMING;
    case FaceCategory.SUB_FLOORING:
      return SheetGeometryCategory.SUB_FLOORING;
    case FaceCategory.FOUNDATION_FOOTING:
      return SheetGeometryCategory.FOUNDATION_FOOTING;
    case FaceCategory.FOUNDATION_GARAGE_CURB:
      return SheetGeometryCategory.FOUNDATION_CURB;
    case FaceCategory.FOUNDATION_SLAB:
      return SheetGeometryCategory.SLAB;
    case FaceCategory.FOUNDATION_STEM_WALL:
      return SheetGeometryCategory.STEM_WALL;
    case FaceCategory.FOUNDATION_PIER:
      return SheetGeometryCategory.FOUNDATION_PIER;
    case FaceCategory.GROUND:
      return undefined;
    case FaceCategory.PORCH_DECORATION:
    case FaceCategory.PORCH_EXTERIOR:
      return SheetGeometryCategory.PORCH;
    case FaceCategory.RAILING:
      return SheetGeometryCategory.RAILING;
    case FaceCategory.PROP_APPLIANCES:
      return SheetGeometryCategory.PROP_APPLIANCES;
    case FaceCategory.PROP_FIXTURES:
      return SheetGeometryCategory.PROP_FIXTURES;
    case FaceCategory.PROP_FURNITURE:
      return SheetGeometryCategory.PROP_FURNITURE;
    case FaceCategory.PROP_OTHER:
      return SheetGeometryCategory.PROP_OTHER;
    case FaceCategory.PROP_FOLIAGE:
      return SheetGeometryCategory.PROP_FOLIAGE;
    case FaceCategory.ROOF_BOTTOM:
      return SheetGeometryCategory.ROOF; // TODO: Categorize as trim instead? This category has some geometry that looks bugged if it's isolated, so it seems safest to group it with roofs for now.
    case FaceCategory.ROOF_DORMER_WALL:
      return SheetGeometryCategory.WALLS;
    case FaceCategory.ROOF_RIDGE_VENT:
      return SheetGeometryCategory.ROOF_RIDGE_VENT;
    case FaceCategory.ROOF_RIDGE_CAP:
      return SheetGeometryCategory.ROOF; // Note that this is not currently part of the filterable geometry.
    case FaceCategory.ROOF_TOP:
    case FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY:
      return SheetGeometryCategory.ROOF;
    case FaceCategory.ROOF_FRIEZE_BOARD:
      return SheetGeometryCategory.ROOF_FRIEZE_BOARD;
    case FaceCategory.ROOF_FASCIA_BOARD:
      return SheetGeometryCategory.ROOF_FASCIA_BOARD;
    case FaceCategory.ROOF_FASCIA_TOP_TRIM:
      return SheetGeometryCategory.ROOF_FASCIA_TOP_TRIM;
    case FaceCategory.ROOF_SOFFIT:
      return SheetGeometryCategory.ROOF_SOFFIT;
    case FaceCategory.ROOF_BELLY_BAND_BOARD:
      return SheetGeometryCategory.ROOF_BELLY_BAND_BOARD;
    case FaceCategory.ROOF_WALL_BASE_BOARD:
      return SheetGeometryCategory.ROOF_WALL_BASE_BOARD;
    case FaceCategory.ROOF_EXPOSED_RAFTER:
      return SheetGeometryCategory.ROOF_EXPOSED_RAFTER;
    case FaceCategory.ROOF_GABLE_VENT:
      return SheetGeometryCategory.ROOF_GABLE_VENT;
    case FaceCategory.ROOF_WALL_EXTERIOR:
      return SheetGeometryCategory.WALLS;
    case FaceCategory.ROOF_WALL_TRIM:
      return SheetGeometryCategory.TRIM;
    case FaceCategory.ROOM_WALL_BREP:
    case FaceCategory.ROOM_WALL_EXTERIOR:
    case FaceCategory.ROOM_WALL_EXTRUSION:
    case FaceCategory.ROOM_WALL_INTERIOR:
      return SheetGeometryCategory.WALLS;
    case FaceCategory.STAIR:
      return SheetGeometryCategory.STAIR;
    case FaceCategory.WAINSCOTING:
    case FaceCategory.WAINSCOTING_TRIM:
      return SheetGeometryCategory.WAINSCOTING;
    case FaceCategory.WALL_FRAMING:
    case FaceCategory.GABLE_END_WALL_FRAMING:
      return SheetGeometryCategory.WALL_FRAMING;
    case FaceCategory.WINDOW_DECORATION:
    case FaceCategory.WINDOW_EXTERIOR:
    case FaceCategory.WINDOW_GRID:
    case FaceCategory.WINDOW_GLASS:
    case FaceCategory.WINDOW_INTERIOR:
    case FaceCategory.WINDOW_SCREEN:
      return SheetGeometryCategory.WINDOWS;
    case FaceCategory.SKYLIGHT_EXTERIOR:
    case FaceCategory.SKYLIGHT_DECORATION:
    case FaceCategory.SKYLIGHT_GLASS:
      return SheetGeometryCategory.SKYLIGHTS;
    case FaceCategory.BASE_TRIM_MOLDING:
    case FaceCategory.CROWN_TRIM_MOLDING:
    case FaceCategory.WALL_PLANE_TRIM_MOLDING:
      return SheetGeometryCategory.TRIM;
    case FaceCategory.COLUMN:
      return SheetGeometryCategory.COLUMN;
    case FaceCategory.ROOF_FRAMING:
      return SheetGeometryCategory.ROOF_FRAMING;
    case FaceCategory.SLAB_FLOORING:
      return SheetGeometryCategory.SLAB;
    // Site geometry categories - not shown in plan sheets
    case FaceCategory.SITE_ROAD:
    case FaceCategory.SITE_GRASS:
    case FaceCategory.SITE_SIDEWALK:
    case FaceCategory.SITE_CURB:
    case FaceCategory.SITE_DRIVEWAY:
    case FaceCategory.SITE_FENCE:
    case FaceCategory.SITE_TOPOGRAPHY:
    case FaceCategory.SITE_WHITEBOX:
    case FaceCategory.SITE_PLANE:
      return undefined;
    default:
      assertUnreachable(category);
  }
}

/**
 * Filter 3D geometry categories according to SheetGeometryCategories from view template properties.
 */
export function viewTemplateLineworkHasFaceCategory(
  categories: readonly SheetGeometryCategory[],
  category: FaceCategory,
): boolean {
  switch (category) {
    case FaceCategory.GROUND:
      return true; // Always included. TODO: Expose control to the user.
    case FaceCategory.ROOF_RIDGE_CAP:
      return false; // Intentionally left out of view template plans.
    case FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY:
      return false; // This geometry exists mostly for export purposes, not used in live plans.
    // Site geometry - not shown in plan sheets
    case FaceCategory.SITE_ROAD:
    case FaceCategory.SITE_GRASS:
    case FaceCategory.SITE_SIDEWALK:
    case FaceCategory.SITE_CURB:
    case FaceCategory.SITE_DRIVEWAY:
    case FaceCategory.SITE_FENCE:
    case FaceCategory.SITE_TOPOGRAPHY:
    case FaceCategory.SITE_WHITEBOX:
    case FaceCategory.SITE_PLANE:
      return false;
    default:
      // It's important that we only check one SheetGeometryCategory here.
      // Each category of faces in the 3D geometry needs to be toggleable and styleable by a single control in view template UI.
      // However, one SheetGeometryCategory can currently map to multiple FaceCategories.
      const sheetGeometryCategory = faceCategoryToSheetGeometryCategory(category);
      assert(sheetGeometryCategory !== undefined);
      return categories.includes(sheetGeometryCategory);
  }
}

const faceCategoryFilterCache = new WeakMap<readonly SheetGeometryCategory[], Set<FaceCategory>>();

/**
 * One Set per categories array. This travels inside plan-set request payloads, whose cache key
 * hashes objects by reference, so a fresh Set would give an unchanged drawing a new key.
 *
 * Shared between callers — treat it as read-only.
 */
export function faceCategoryFilterFromSheetGeometryCategories(
  categories: readonly SheetGeometryCategory[] | undefined,
): Set<FaceCategory> | undefined {
  if (!categories) {
    return undefined;
  }

  const cachedFilter = faceCategoryFilterCache.get(categories);

  if (cachedFilter) {
    return cachedFilter;
  }

  const faceCategoryFilter: Set<FaceCategory> = new Set();
  for (const faceCategory of allFaceCategories) {
    if (viewTemplateLineworkHasFaceCategory(categories, faceCategory)) {
      faceCategoryFilter.add(faceCategory);
    }
  }

  faceCategoryFilterCache.set(categories, faceCategoryFilter);

  return faceCategoryFilter;
}

const hatch3DDefault = true;

const hatchCategoriesCache = new WeakMap<ViewTemplate, SheetGeometryCategory[]>();

const NO_HATCH_CATEGORIES: SheetGeometryCategory[] = [];

/**
 * @returns the set of categories that are hatched in 3D linework views.
 *
 * One array per view template. It is passed to `faceCategoryFilterFromSheetGeometryCategories`,
 * which memoizes on its argument's identity, so a fresh array here would defeat that memo.
 *
 * Shared between callers — treat it as read-only.
 */
export function viewTemplate3DHatchCategories(viewTemplate: ViewTemplate): SheetGeometryCategory[] {
  const cached = hatchCategoriesCache.get(viewTemplate);

  if (cached) {
    return cached;
  }

  const result = computeViewTemplate3DHatchCategories(viewTemplate);
  hatchCategoriesCache.set(viewTemplate, result);

  return result;
}

function computeViewTemplate3DHatchCategories(viewTemplate: ViewTemplate): SheetGeometryCategory[] {
  if (!isLinework3DGeometryView(viewTemplate.viewType)) {
    return NO_HATCH_CATEGORIES;
  }

  if (viewTemplate.viewType === "SECTION_VIEW") {
    // Hatching is disabled in section views.
    // This is mostly because we don't have full support for hatching in section three.js preview, there are some bugs we need to fix before enabling it.
    return NO_HATCH_CATEGORIES;
  }

  function isCategoryHatched(category: SheetGeometryCategory) {
    if (!sheetGeometry3DCategories.includes(category)) {
      return false;
    }
    if (!viewTemplate.styleOverrides) {
      return hatch3DDefault;
    }
    if (!viewTemplate.styleOverrides[category]) {
      return hatch3DDefault;
    }
    if ((viewTemplate.styleOverrides[category].line as LineStyle)?.hatch !== undefined) {
      return (viewTemplate.styleOverrides[category].line as LineStyle).hatch;
    }
    return hatch3DDefault;
  }

  return viewTemplate.categories.filter(isCategoryHatched);
}

/**
 * The line style a category renders with when a View Template carries no override for it, so that
 * style controls can show the weight, color and dash actually drawn instead of reading as empty.
 *
 * Mirrors the cascade in `makeSheetGeometryStyler`, with each view type resolved against the
 * default stylesheet its renderer actually uses. Only elevations and sections are resolved for
 * now -- other view types return {} and leave their controls displaying as they did before.
 */
export function viewTemplateDefaultCategoryLineStyle(
  viewType: ViewTemplate["viewType"],
  category: SheetGeometryCategory,
): LineStyle {
  if (viewType === "ELEVATION_VIEW") {
    return elevationDefaultCategoryStyles[category]?.line ?? {};
  }
  if (viewType === "SECTION_VIEW") {
    return planDefaultCategoryStyles[category]?.line ?? {};
  }
  return {};
}

export function viewTemplateDefaultStyle(
  viewType: ViewTemplate["viewType"],
  category: SheetGeometryCategory,
): SheetGeometryCategoryStyle {
  if (viewType !== "VIEW_3D" && viewType !== "ELEVATION_VIEW" && viewType !== "SECTION_VIEW") {
    return {};
  }

  if (sheetGeometry3DCategories.includes(category)) {
    return { line: { hatch: hatch3DDefault } };
  }
  return {};
}

export const viewTemplateTypeToPrettyName: {
  [key in ViewportViewType]: string;
} = {
  PLAN_VIEW: "Plan View Template",
  ELEVATION_VIEW: "Elevation View Template",
  VIEW_3D: "Perspective View Template",
  SECTION_VIEW: "Section View Template",
  DRAWING: "Drawing View Template",
};

export function viewTemplateSetStyleOverrides(
  styleOverrides: SheetGeometryCategoryStyleSheets,
  category: SheetGeometryCategory,
  styleType: keyof SheetGeometryCategoryStyle,
  key: string,
  value: string | number | boolean,
): SheetGeometryCategoryStyleSheets {
  const newStyleOverrides = { ...styleOverrides };
  newStyleOverrides[category] = {
    ...newStyleOverrides[category],
    [styleType]: {
      ...(newStyleOverrides[category]?.[styleType] ?? {}),
      [key]: value,
    },
  };
  return newStyleOverrides;
}
